/* =========================================================
   IDENTITY LUXE — Motion layer
   Lenis smooth scroll + GSAP ScrollTrigger reveals.
   Off for "reduce motion" users and inside the theme editor.
   PERF 2026-10:
   - Lenis is downloaded only on desktop (mouse), after the page is ready.
   - Phones/tablets keep the one-shot reveals but skip every "scrub"
     effect (parallax, hero fade, scroll progress bar) that ran on each
     scroll frame and made scrolling lag.
   ========================================================= */
(() => {
  const html = document.documentElement;
  const done = () => html.classList.remove('il-motion-pending');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = window.Shopify && window.Shopify.designMode;

  /* ---------- Split headings into words (always — used for styling too) ---------- */
  function splitWords(el) {
    if (!el || el.dataset.ilSplit) return [];
    el.dataset.ilSplit = '1';
    const words = [];
    let line = 1;
    const walk = (node) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === 3) {
          // RTL: animate whole lines so mixed Arabic/Latin text keeps its natural order
          const rtl = (el.closest('[dir]') || html).getAttribute('dir') === 'rtl';
          const text = child.textContent;
          const parts = rtl ? (text.trim() ? [text.trim()] : []) : text.split(/(\s+)/);
          const frag = document.createDocumentFragment();
          parts.forEach((p) => {
            if (!p) return;
            if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(' ')); return; }
            const w = document.createElement('span');
            w.className = 'il-w il-w--l' + line;
            const i = document.createElement('span');
            i.className = 'il-w__i';
            i.textContent = p;
            w.appendChild(i);
            frag.appendChild(w);
            words.push(i);
          });
          child.replaceWith(frag);
        } else if (child.nodeName === 'BR') {
          line += 1;
        } else if (child.nodeType === 1) {
          walk(child);
        }
      });
    };
    walk(el);
    if (line > 1) el.classList.add('il-split--multi');
    return words;
  }
  document.querySelectorAll('.il-hero__title, .il-story__title, .il-fp__title').forEach(splitWords);

  if (reduce || designMode || !window.gsap) { done(); return; }

  const gsap = window.gsap;
  const ST = window.ScrollTrigger;
  if (ST) gsap.registerPlugin(ST);
  html.classList.add('il-motion');
  const touch = window.matchMedia('(pointer: coarse), (max-width: 989px)').matches;
  const scrub = !touch;   /* scroll-linked effects: desktop only */

  /* ---------- Lenis smooth scroll (desktop only, loaded on demand) ---------- */
  const startLenis = () => {
    if (!window.Lenis || window.ilLenis) return;
    const lenis = new window.Lenis({
      lerp: 0.085,
      wheelMultiplier: 0.95,
      smoothWheel: true,
      allowNestedScroll: true,
      anchors: { offset: -90 },
      prevent: (node) => !!(node.closest && node.closest(
        'cart-drawer, .drawer, menu-drawer, .menu-drawer, modal-dialog, .product-popup-modal, dialog, ' +
        '.quick-view, .product-modal-content, .predictive-search, details-modal, [data-lenis-prevent]'
      ))
    });
    window.ilLenis = lenis;
    if (ST) { lenis.on('scroll', ST.update); ST.refresh(); }
    gsap.ticker.add((t) => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);

    // pause smooth scroll whenever the theme locks the page (drawers, popups, menus)
    const sync = () => {
      const wide = window.innerWidth >= 990;
      const locked = [...document.body.classList].some((c) => {
        if (c.indexOf('overflow-hidden') !== 0) return false;
        if (wide && (c === 'overflow-hidden-mobile' || c === 'overflow-hidden-tablet')) return false; // e.g. language menu
        return true;
      }) || html.classList.contains('il-fb-sheet-open');
      locked ? lenis.stop() : lenis.start();
    };
    new MutationObserver(sync).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    new MutationObserver(sync).observe(html, { attributes: true, attributeFilter: ['class'] });
    html.classList.add('il-lenis');
  };
  if (!touch) {
    if (window.Lenis) startLenis();
    else {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/lenis@1.3.26/dist/lenis.min.js';
      s.async = true;
      s.onload = startLenis;
      document.head.appendChild(s);
    }
  }

  /* ---------- Scroll progress hairline (desktop) ---------- */
  if (ST && scrub) {
    const bar = document.createElement('div');
    bar.className = 'il-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
    gsap.to(bar, { scaleX: 1, ease: 'none', scrollTrigger: { start: 0, end: 'max', scrub: 0.3 } });
  }

  const ease = 'expo.out';
  const reveal = (targets, vars, trigger, start) => {
    const els = gsap.utils.toArray(targets);
    if (!els.length || !ST) return;
    gsap.from(els, Object.assign({
      autoAlpha: 0, y: 40, duration: 1.1, ease, stagger: 0.08,
      scrollTrigger: { trigger: trigger || els[0], start: start || 'top 85%', once: true }
    }, vars));
  };

  /* ---------- Hero ----------
     FIX 2026-10: the scroll fade used gsap.to(), which saved the copy's start opacity
     while <html> still had .il-motion-pending (opacity 0) — so on the live site the
     heading/text stayed at 0–20% opacity. Now: intro tweens render first, then the
     pending class is removed, the scroll fade uses explicit fromTo values, and a
     safety net forces the copy visible. Zoom reduced + parallax removed (it cropped
     the image on desktop). */
  document.querySelectorAll('.il-hero').forEach((hero) => {
    const img = hero.querySelector('.il-hero__img, .il-hero__video');
    const words = hero.querySelectorAll('.il-hero__title .il-w__i');
    const content = hero.querySelector('.il-hero__content');
    const others = hero.querySelectorAll('.il-hero__content > :not(.il-hero__title)');
    const tl = gsap.timeline({ defaults: { ease }, delay: 0.1 });
    if (img) tl.fromTo(img, { scale: 1.05 }, { scale: 1, duration: 1.8, ease: 'power3.out', clearProps: 'transform' }, 0);
    if (words.length) tl.from(words, { yPercent: 115, rotate: 4, duration: 1.3, stagger: 0.07 }, 0.25);
    if (others.length) tl.from(others, { autoAlpha: 0, y: 26, duration: 1, stagger: 0.1 }, 0.55);
    done();

    const trust = hero.parentElement && hero.parentElement.querySelectorAll('.il-trustbar__item');
    if (trust && trust.length) tl.from(trust, { autoAlpha: 0, y: 18, duration: 0.9, stagger: 0.08 }, 0.9);

    if (ST && scrub && content) {
      gsap.fromTo(content, { y: 0, autoAlpha: 1 }, {
        y: -50, autoAlpha: 0.35, ease: 'none', immediateRender: false,
        scrollTrigger: { trigger: hero, start: 'center center', end: 'bottom top', scrub: true }
      });
    }

    // safety net: never leave the hero copy hidden
    setTimeout(() => {
      if (window.scrollY > 40) return;
      tl.progress(1);
      gsap.set(content, { autoAlpha: 1, y: 0 });
      gsap.set(others, { autoAlpha: 1, y: 0 });
      gsap.set(words, { yPercent: 0, rotate: 0 });
    }, 3200);
  });
  if (!document.querySelector('.il-hero')) done();

  if (!ST) return;

  /* ---------- Section heads ---------- */
  document.querySelectorAll('.il-head').forEach((head) => {
    const tl = gsap.timeline({ scrollTrigger: { trigger: head, start: 'top 85%', once: true }, defaults: { ease } });
    const kids = head.querySelectorAll('.il-eyebrow, .il-head__title, .il-head__text');
    tl.from(kids, { autoAlpha: 0, y: 30, duration: 1.1, stagger: 0.09 });
    const orn = head.querySelector('.il-ornament');
    if (orn) tl.from(orn, { scaleX: 0, duration: 1.2, ease: 'power3.inOut' }, 0.15);
  });

  /* ---------- Collection cards ---------- */
  document.querySelectorAll('.il-collections__grid').forEach((grid) => {
    const cards = grid.querySelectorAll('.il-col-card');
    gsap.from(cards, {
      clipPath: 'inset(100% 0% 0% 0%)', duration: 1.4, ease: 'expo.inOut', stagger: 0.12,
      scrollTrigger: { trigger: grid, start: 'top 82%', once: true },
      clearProps: 'clipPath'
    });
    cards.forEach((card, i) => {
      const img = card.querySelector('.il-col-card__img');
      const body = card.querySelector('.il-col-card__body');
      if (img) gsap.from(img, { scale: 1.3, duration: 1.8, ease: 'expo.out', delay: 0.12 * i, scrollTrigger: { trigger: grid, start: 'top 82%', once: true }, clearProps: 'transform' });
      if (body) gsap.from(body, { autoAlpha: 0, y: 24, duration: 1, delay: 0.5 + 0.12 * i, ease, scrollTrigger: { trigger: grid, start: 'top 82%', once: true } });
    });
  });

  /* ---------- Story banner ---------- */
  document.querySelectorAll('.il-story__frame').forEach((frame) => {
    const img = frame.querySelector('.il-story__img');
    if (img && scrub) {
      gsap.set(img, { scale: 1.15 });
      gsap.fromTo(img, { yPercent: -7 }, { yPercent: 7, ease: 'none', scrollTrigger: { trigger: frame, start: 'top bottom', end: 'bottom top', scrub: true } });
    }
    gsap.from(frame, { clipPath: 'inset(8% 6% 8% 6% round 4px)', duration: 1.6, ease: 'expo.out', scrollTrigger: { trigger: frame, start: 'top 85%', once: true }, clearProps: 'clipPath' });
    const words = frame.querySelectorAll('.il-story__title .il-w__i');
    const tl = gsap.timeline({ scrollTrigger: { trigger: frame, start: 'top 70%', once: true }, defaults: { ease } });
    if (words.length) tl.from(words, { yPercent: 115, duration: 1.2, stagger: 0.06 });
    tl.from(frame.querySelectorAll('.il-story__content > :not(.il-story__title)'), { autoAlpha: 0, y: 24, duration: 1, stagger: 0.1 }, 0.3);
  });

  /* ---------- Family Pack panel ---------- */
  document.querySelectorAll('.il-fp__panel').forEach((panel) => {
    const tl = gsap.timeline({ scrollTrigger: { trigger: panel, start: 'top 80%', once: true }, defaults: { ease } });
    tl.from(panel, { clipPath: 'inset(6% 4% 6% 4% round 18px)', duration: 1.5, ease: 'expo.out', clearProps: 'clipPath' });
    const words = panel.querySelectorAll('.il-fp__title .il-w__i');
    if (words.length) tl.from(words, { yPercent: 115, duration: 1.1, stagger: 0.06 }, 0.2);
    tl.from(panel.querySelectorAll('.il-fp__sub, .il-fp__text'), { autoAlpha: 0, y: 20, duration: 0.9, stagger: 0.08 }, 0.35);
    tl.from(panel.querySelectorAll('.il-fp-tier'), { autoAlpha: 0, y: 30, scale: 0.94, duration: 0.9, stagger: 0.1 }, 0.45);
    tl.from(panel.querySelectorAll('.il-fp__content > .il-btn'), { autoAlpha: 0, y: 20, duration: 0.8 }, 0.7);
    const badge = panel.querySelector('.il-fp__badge');
    if (badge) tl.from(badge, { scale: 0, rotate: -120, duration: 1.2, ease: 'back.out(1.6)' }, 0.5);
    const img = panel.querySelector('.il-fp__img');
    if (img && scrub) gsap.fromTo(img, { scale: 1.15, yPercent: -5 }, { scale: 1.05, yPercent: 5, ease: 'none', scrollTrigger: { trigger: panel, start: 'top bottom', end: 'bottom top', scrub: true } });
  });
  reveal('.il-fp__steps li', { y: 26 }, '.il-fp__steps');

  /* ---------- Features / steps / builder ---------- */
  document.querySelectorAll('.il-features__grid').forEach((g) => reveal(g.children, { y: 34 }, g));
  document.querySelectorAll('.il-fb__steps').forEach((g) => reveal(g.children, { y: 24 }, g));
  document.querySelectorAll('.il-fb .il-fp-tiers').forEach((g) => reveal(g.children, { y: 24, scale: 0.96 }, g));
  if (document.querySelector('.il-fb-card')) {
    gsap.set('.il-fb-card', { autoAlpha: 0, y: 40 });
    ST.batch('.il-fb-card', {
      start: 'top 95%',
      once: true,
      onEnter: (els) => gsap.to(els, { autoAlpha: 1, y: 0, duration: 1, ease, stagger: 0.07, overwrite: true })
    });
    // safety net: never leave a card invisible
    setTimeout(() => gsap.utils.toArray('.il-fb-card').forEach((c) => {
      if (c.getBoundingClientRect().top < window.innerHeight && gsap.getProperty(c, 'opacity') < 1) gsap.to(c, { autoAlpha: 1, y: 0, duration: 0.6 });
    }), 2500);
  }

  /* ---------- Generic theme sections (headings only, theme handles the rest) ---------- */
  reveal('.testimonials .section-header, .collection-tabs .section-header', { y: 30 });

  /* ---------- Magnetic buttons (desktop) ---------- */
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    document.querySelectorAll('.il-btn').forEach((btn) => {
      const xTo = gsap.quickTo(btn, 'x', { duration: 0.6, ease: 'elastic.out(1, 0.4)' });
      const yTo = gsap.quickTo(btn, 'y', { duration: 0.6, ease: 'elastic.out(1, 0.4)' });
      btn.addEventListener('mousemove', (e) => {
        const r = btn.getBoundingClientRect();
        xTo((e.clientX - r.left - r.width / 2) * 0.22);
        yTo((e.clientY - r.top - r.height / 2) * 0.35);
      });
      btn.addEventListener('mouseleave', () => { xTo(0); yTo(0); });
    });
  }

  /* refresh positions once images and fonts settle */
  window.addEventListener('load', () => ST.refresh());
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => ST.refresh());
})();
